import { AppDisplay } from '@ui/applications';
import { ColumnType } from 'antd/es/table';
import { DataSecurityIncident } from '@sonar-software/sonar-core';
import dayjs from 'dayjs';
import dayjsduration from 'dayjs/plugin/duration';
import { getTimeRecordDate } from '@sonar-software/sonar-ts-lib';
import { NavigateFunction } from 'react-router';
import React from 'react';
import { SonarText } from '@ui/text';
import { When } from 'react-if';

import { Button, Space } from 'antd';
import { COPY_GENERIC, COPY_SPECIFIC, DATA_SECURITY_DASHBOARD, FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { IncidentEventType, IncidentEventTypeTag } from './IncidentEventTypeTag';

dayjs.extend(dayjsduration);

type ColumnDefinition = ColumnType<DataSecurityIncident>;

export const appDisplayColumn: ColumnDefinition = {
	dataIndex: 'applicationId',
	key: 'applicationId',
	render: (_, { applicationId, applicationLabel, applicationName }) =>
		<AppDisplay
			appId={ applicationId }
			appLabel={ applicationLabel }
			appName={ applicationName } />,
	title: COPY_SPECIFIC.APPLICATION,
	width: 170
};

export const eventTypeColumn: ColumnDefinition = {
	align: 'center',
	dataIndex: 'eventType',
	key: 'eventType',
	render: (eventType: IncidentEventType) => <IncidentEventTypeTag eventType={ eventType } />,
	title: DATA_SECURITY_DASHBOARD.EVENT_TYPE,
	width: 80
};

export const startColumn: ColumnDefinition = {
	align: 'right',
	dataIndex: 'eventStart',
	key: 'eventStart',
	render: (eventStart: string) => <SonarText>{ getTimeRecordDate(eventStart) }</SonarText>,
	title: COPY_GENERIC.START,
	width: 120
};

export const endColumn: ColumnDefinition = {
	align: 'right',
	dataIndex: 'eventEnd',
	key: 'eventEnd',
	render: (eventEnd: string | null) => (eventEnd ? <SonarText>{ getTimeRecordDate(eventEnd) }</SonarText> : null),
	title: COPY_GENERIC.END,
	width: 120
};

export const durationColumn: ColumnDefinition = {
	align: 'right',
	key: 'duration',
	render: (_, { eventStart, eventEnd }) => {
		const start = dayjs(eventStart);
		const end = dayjs(eventEnd ?? undefined);
		const diff = end.diff(start);

		return (
			<SonarText>
				{ dayjs.duration(diff).format('D [Day] H [Hrs] m [min]')
					.replace(/\b0 Day\b/u, '')
					.replace(/\b0 Hrs\b/u, '')
					.replace(/\b0 min\b/u, '') }
			</SonarText>);
	},
	title: COPY_GENERIC.DURATION,
	width: 120
};

export const actionColumn = (
	navigate: NavigateFunction,
	appSelected: boolean
): ColumnDefinition => ({
	dataIndex: 'applicationId',
	key: 'actions',
	render: (applicationId) =>
		<Space size={ 8 }>
			<When condition={ !appSelected }>
				<Button
					onClick={ () => navigate(`${FULL_ROUTES.DATA_SECURITY}/${applicationId}`) }
					type='primary'>
					{ COPY_SPECIFIC.REVIEW }
				</Button>
			</When>
		</Space>,
	title: COPY_SPECIFIC.ACTION,
	width: appSelected ? 0 : 120
});

export function getColumns(
	appSelected: boolean,
	navigate: NavigateFunction
): Array<ColumnDefinition> {
	return [
		appDisplayColumn,
		eventTypeColumn,
		startColumn,
		endColumn,
		durationColumn,
		actionColumn(navigate, appSelected)
	];
}
